import type * as React from 'react';
import {useIsomorphicLayoutEffect} from '../../util';
import {setRef} from '../assignProps';
import {$revision, $shared} from '../constants';
import type {ViewInspection} from './index';
import {scheduleInspectionPublication} from './inspectionService';
import type {View} from '../types';

/** Live inspection data from a committed render. IDs are local to this engine. */
export interface CommittedViewInspection {
  readonly instanceId: number;
  readonly inspection: ViewInspection;
}

type SubviewOwner = NonNullable<ViewInspection['subview']>;
type Subviews = View.Subviews<Record<string, unknown>>;

interface InspectionInstance {
  readonly instanceId: number;
  readonly ref: React.RefCallback<unknown>;
  revision: number;
  lastInspection?: WeakSet<ViewInspection>;
  element?: Element;
  committed?: {
    view: CommittedViewInspection;
    parent?: InspectionInstance;
  };
}

let nextInstanceId = 1;
let instancesByShared:
  | WeakMap<object, WeakMap<object, InspectionInstance>>
  | undefined;
let instancesByElement: WeakMap<Element, Set<InspectionInstance>> | undefined;
let committedById: Map<number, CommittedViewInspection> | undefined;
let ownersBySubview: WeakMap<object, SubviewOwner> | undefined;

function getOrCreateInstance<Props>(
  revision: View.ViewRevision<Props>,
): InspectionInstance {
  // Flattened views recreate their instance objects; shared state and config
  // identify the same logical view across those renders.
  const shared = revision.instance[$shared] as object;
  instancesByShared ??= new WeakMap();
  let instances = instancesByShared.get(shared);
  if (!instances) {
    instances = new WeakMap();
    instancesByShared.set(shared, instances);
  }
  const existing = instances.get(revision.instance.config);
  if (existing) {
    return existing;
  }

  const instance: InspectionInstance = {
    instanceId: nextInstanceId++,
    revision: 0,
    ref(value) {
      if (instance.element) {
        const attached = instancesByElement?.get(instance.element);
        attached?.delete(instance);
        if (!attached?.size) {
          instancesByElement?.delete(instance.element);
        }
      }
      // Avoid instanceof so elements from another window work too.
      instance.element =
        value && typeof value === 'object' && (value as Node).nodeType === 1
          ? (value as Element)
          : undefined;
      if (instance.element) {
        instancesByElement ??= new WeakMap();
        let attached = instancesByElement.get(instance.element);
        if (!attached) {
          attached = new Set();
          instancesByElement.set(instance.element, attached);
        }
        attached.add(instance);
      }
      scheduleInspectionPublication(instance);
    },
  };
  instances.set(revision.instance.config, instance);
  return instance;
}

export function getInstanceId<Props>(
  revision: View.ViewRevision<Props>,
): number {
  return getOrCreateInstance(revision).instanceId;
}

/** Record local handles; forwarded handles retain their original ownership. */
export function recordSubviewOwners<Props>(
  revision: View.ViewRevision<Props>,
  subviews: Subviews,
  forwardedSubviews: Record<string, unknown> | undefined,
): void {
  const {instanceId: ownerInstanceId} = getOrCreateInstance(revision);
  for (const name of Object.keys(subviews)) {
    const subview = subviews[name];
    if (subview && subview !== forwardedSubviews?.[name]) {
      ownersBySubview ??= new WeakMap();
      ownersBySubview.set(subview, {ownerInstanceId, name});
    }
  }
}

/** DOM views can inherit through wrappers before reaching a subview handle. */
export function getSubviewOwner<Props>(
  revision: View.ViewRevision<Props>,
): SubviewOwner | undefined {
  type PropsWithInherits = {
    inherits?: View.Inherits | View.Subview<unknown>;
  };
  let inherits = (revision.initialProps as PropsWithInherits).inherits;
  while (inherits) {
    const owner = ownersBySubview?.get(inherits);
    if (owner) {
      return owner;
    }
    const inheritedRevision = (inherits as View.Inherits)[$revision];
    inherits = (
      inheritedRevision?.initialProps as PropsWithInherits | undefined
    )?.inherits;
  }
  return undefined;
}

/** Merge a stable debug callback through the engine's normal ref processor. */
export function attachElementRef<Props>(
  revision: View.ViewRevision<Props>,
): void {
  setRef(
    getOrCreateInstance(revision).ref,
    revision as unknown as View.ViewRevision<
      Record<string, React.Ref<unknown>>
    >,
    'ref',
  );
}

function getAttachedInstanceIds(element: Element): number[] {
  return Array.from(
    instancesByElement?.get(element) ?? [],
    ({instanceId}) => instanceId,
  );
}

/** Publish only committed data; cleanup also handles debug toggles and replay. */
function commitInspection<Props>(
  revision: View.ViewRevision<Props>,
  inheritedRevision: View.ViewRevision<Props> | void,
): (() => void) | undefined {
  const {inspection} = revision;
  if (!inspection) {
    return undefined;
  }
  const instance = getOrCreateInstance(revision);
  // Effect replay republishes the same render without inventing a new revision.
  if (!instance.lastInspection?.has(inspection)) {
    instance.revision++;
    // Preserve replay identity without retaining a retired inspection payload.
    instance.lastInspection = new WeakSet([inspection]);
  }
  instance.committed = {
    view: {instanceId: instance.instanceId, inspection},
    parent: inheritedRevision?.inspection
      ? getOrCreateInstance(inheritedRevision)
      : undefined,
  };
  committedById ??= new Map();
  committedById.set(instance.instanceId, instance.committed.view);
  scheduleInspectionPublication(instance, getAttachedInstanceIds);

  return () => {
    if (instance.committed?.view.inspection === inspection) {
      instance.committed = undefined;
      committedById?.delete(instance.instanceId);
      scheduleInspectionPublication(instance);
    }
  };
}

/** React cleans up and replays the layout effect when Suspense hides and reveals. */
export function InspectionCommit<Props>({
  revision,
  inheritedRevision,
}: {
  revision: View.ViewRevision<Props>;
  inheritedRevision: View.ViewRevision<Props> | void;
}): null {
  useIsomorphicLayoutEffect(
    () => commitInspection(revision, inheritedRevision),
    [revision, inheritedRevision],
  );

  return null;
}

/** Retrieve the latest committed inspection, or undefined after cleanup. */
export function getViewInspection(
  instanceId: number,
): CommittedViewInspection | undefined {
  return committedById?.get(instanceId);
}

/**
 * List committed views, or find the nearest inspected DOM boundary and its
 * inheritance chain, from the DOM view outward. Raw descendants use their parent.
 */
export function getViewInspections(
  element?: Element,
): readonly CommittedViewInspection[] {
  if (!committedById?.size) {
    return [];
  }
  if (!element) {
    return Array.from(committedById.values());
  }
  for (let node: Element | null = element; node; node = node.parentElement) {
    const attached = instancesByElement?.get(node);
    if (attached) {
      const views = new Map<number, CommittedViewInspection>();
      // Ref merging attaches the innermost view last.
      for (const instance of Array.from(attached).reverse()) {
        let current: InspectionInstance | undefined = instance;
        while (current?.committed && !views.has(current.instanceId)) {
          views.set(current.instanceId, current.committed.view);
          current = current.committed.parent;
        }
      }
      if (views.size) {
        return Array.from(views.values());
      }
    }
  }
  return [];
}
